import React from 'react';
import { ChevronRight, Folder } from 'lucide-react';

/**
 * Breadcrumb item interface
 */
interface BreadcrumbItem {
  name: string;
  path: string;
  isRoot?: boolean;
}

/**
 * Props for FolderBreadcrumb component
 */
interface FolderBreadcrumbProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  className?: string;
}

/**
 * FolderBreadcrumb Component
 * Displays the current folder path as clickable breadcrumbs
 * Supports navigation to parent folders
 * Shows visual hierarchy with separators
 */
const FolderBreadcrumb: React.FC<FolderBreadcrumbProps> = ({
  currentPath,
  onNavigate,
  className = ''
}) => {
  // Build breadcrumb items from current path
  const buildBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [
      { name: 'Matter', path: '', isRoot: true }
    ];

    if (currentPath && currentPath !== '' && currentPath !== '/') {
      // Split path and build cumulative paths
      const pathParts = currentPath.split('/').filter(part => part !== '');
      let cumulativePath = '';

      pathParts.forEach((part) => {
        cumulativePath += `/${part}`;
        breadcrumbs.push({
          name: part,
          path: cumulativePath,
          isRoot: false
        });
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = buildBreadcrumbs();

  // Handle breadcrumb click
  const handleBreadcrumbClick = (item: BreadcrumbItem) => {
    onNavigate(item.path);
  };

  return (
    <div className={`flex items-center gap-1 text-sm ${className}`}>
      {breadcrumbs.map((item, index) => (
        <React.Fragment key={item.path}>
          {/* Separator (except for first item) */}
          {index > 0 && (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}

          {/* Breadcrumb item */}
          <button
            onClick={() => handleBreadcrumbClick(item)}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
              index === breadcrumbs.length - 1
                ? 'text-white bg-blue-900'  // Current folder - highlighted
                : 'text-gray-400 hover:text-white hover:bg-gray-700'  // Clickable parent folders
            }`}
            disabled={index === breadcrumbs.length - 1}  // Disable current folder
          >
            {/* Icon */}
            {item.isRoot ? (
              <Folder className="w-3.5 h-3.5" />
            ) : (
              <Folder className="w-3.5 h-3.5" />
            )}

            {/* Name */}
            <span className="max-w-[150px] truncate">
              {item.name}
            </span>
          </button>
        </React.Fragment>
      ))}

      {/* Path depth indicator */}
      {breadcrumbs.length > 1 && (
        <div className="ml-2 text-xs text-gray-500">
          {breadcrumbs.length - 1}/3 levels
        </div>
      )}
    </div>
  );
};

export default FolderBreadcrumb;
